/// <amd-module name="Artifacts/FeedSettings/components/FeedSettingsHub" />

import { DetailsTabContainer } from "Artifacts/FeedSettings/containers/DetailsTabContainer";
import { PermissionsTabContainer } from "Artifacts/FeedSettings/containers/PermissionsTabContainer";
import { UpstreamsTabContainer } from "Artifacts/FeedSettings/containers/UpstreamsTabContainer";
import { ViewsTabContainer } from "Artifacts/FeedSettings/containers/ViewsTabContainer";
import * as Res from "Artifacts/FeedSettings/Resources";
import { IFeedSettings } from "Artifacts/FeedSettings/types";
import { DefaultLoader } from "Artifacts/Root/components/DefaultLoader";
import { ComponentState, FeedOperationPermissions } from "Artifacts/Root/Constants";
import { getFeedIdentifier, isPublicFeed } from "Artifacts/Root/helpers/FeedHelpers";
import { isUserFeedAdmin, isUserPermittedToUseOperation } from "Artifacts/Root/helpers/UserHelpers";
import { ITabDefinition, useRouteTabManager } from "Artifacts/Root/hooks/RouteTabManager";
import {
  detailsSettingsRoute,
  feedHubRoute,
  permissionsSettingsRoute,
  upstreamsSettingsRoute,
  viewsSettingsRoute,
} from "Artifacts/Root/routing/Constants";
import { IFeed } from "Artifacts/Root/types";
import { ViewIdentifier } from "Artifacts/Root/types/AzureArtifacts";
import { IRoutedTabContainerProps } from "Artifacts/Root/types/IRoutedTabContainerProps";
import * as React from "react";
import { generatePath, useHistory } from "react-router-dom";
import { ScreenSize } from "VSS/Core/Util/Screen";
import * as StringUtils from "VSS/Core/Util/String";
import { IUnsavedChangesService } from "VSS/Features/PlatformUI/UnsavedChangesService";
import { useComponentContext } from "VSS/Platform/Layout";
import { Header } from "VSSUI/Header";
import { MenuItemType } from "VSSUI/Menu";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { Page } from "VSSUI/Page";
import { Tab, TabBar } from "VSSUI/Tabs";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";

const defaultTab = "details";

const getTabDefinitions = (
  isUpstreamForPublicFeedsAllowed: boolean,
  isDeleted?: boolean,
  isPublicFeed?: boolean,
): ITabDefinition<IRoutedTabContainerProps>[] => {
  const tabs: ITabDefinition<IRoutedTabContainerProps>[] = [
    {
      tabId: "details",
      name: Res.FeedSettings_DetailsTabName,
      route: detailsSettingsRoute,
      component: DetailsTabContainer,
    },
    {
      tabId: "permissions",
      name: Res.FeedSettings_PermissionsTabName,
      route: permissionsSettingsRoute,
      component: PermissionsTabContainer,
    },
  ];

  if (isDeleted) {
    return tabs;
  }

  tabs.push({
    tabId: "views",
    name: Res.FeedSettings_ViewsTabName,
    route: viewsSettingsRoute,
    component: ViewsTabContainer,
  });

  if (isPublicFeed && !isUpstreamForPublicFeedsAllowed) {
    return tabs;
  }

  tabs.push({
    tabId: "upstreams",
    name: Res.FeedSettings_UpstreamSourcesTabName,
    route: upstreamsSettingsRoute,
    component: UpstreamsTabContainer,
  });

  return tabs;
};

export interface IFeedSettingsHubProps {
  componentState: ComponentState;
  feed?: IFeed;
  view?: ViewIdentifier;
  settings?: IFeedSettings;
  deleteFeed: () => void;
  restoreFeed: () => void;
  permanentDeleteFeed: () => void;
  isUpstreamsDirty: boolean;
  isUpstreamForPublicFeedsAllowed: boolean;
}

export const FeedSettingsHub: React.FC<IFeedSettingsHubProps> = ({
  componentState,
  feed,
  view,
  settings,
  deleteFeed,
  restoreFeed,
  permanentDeleteFeed,
  isUpstreamsDirty,
  isUpstreamForPublicFeedsAllowed,
}) => {
  const tabDefinitions = React.useMemo(() => {
    return getTabDefinitions(
      isUpstreamForPublicFeedsAllowed,
      feed?.isDeleted,
      feed && isPublicFeed(feed),
    );
  }, [feed, isUpstreamForPublicFeedsAllowed]);
  const history = useHistory();
  const [selectedTab, changeTab, CurrentTabComponent] = useRouteTabManager<
    IRoutedTabContainerProps
  >(tabDefinitions, defaultTab);

  const { pageContext } = useComponentContext();
  const unsavedChangesSvc = pageContext.getService<IUnsavedChangesService>(
    "IUnsavedChangesService",
  );
  React.useEffect(() => {
    const doc = {
      isDirty: () => isUpstreamsDirty,
      getDirtyMessage: (): string => Res.UpstreamsTab_PendingChangesAlertMessage,
    };
    unsavedChangesSvc.register(doc);

    return () => {
      unsavedChangesSvc.unregister(doc);
    };
  }, [isUpstreamsDirty]);

  return (
    <DefaultLoader state={componentState}>
      {feed && (
        <Page>
          {settings && !isUserFeedAdmin(feed) && (
            <MessageBar severity={MessageBarSeverity.Warning}>
              {Res.FeedSettings_UserNotAdmin_Warning}
            </MessageBar>
          )}
          {settings && feed.isDeleted && settings.scheduledPermanentDeleteDate && (
            <MessageBar severity={MessageBarSeverity.Error}>
              {StringUtils.format(
                Res.FeedSettings_DeletedFeed_MessageBarMessage,
                new Date(settings.scheduledPermanentDeleteDate).toLocaleDateString(),
              )}
            </MessageBar>
          )}
          <ScreenSizeObserver>
            {(screenSizeProps: { screenSize: ScreenSize }) => {
              const xsScreen = screenSizeProps.screenSize === ScreenSize.xsmall;
              return (
                <Header
                  title={Res.FeedSettings_Header_Title}
                  backButtonProps={{
                    onClick: () =>
                      isUpstreamsDirty
                        ? unsavedChangesSvc.triggerNavigateAway(() => {
                            history.push(
                              generatePath(feedHubRoute, {
                                feedIdentifier: getFeedIdentifier(feed, view),
                              }),
                            );
                          })
                        : history.push(
                            generatePath(feedHubRoute, {
                              feedIdentifier: getFeedIdentifier(feed, view),
                            }),
                          ),
                  }}
                  commandBarItems={
                    feed.isDeleted
                      ? [
                          {
                            id: "restore-feed",
                            text: Res.DetailsTab_RestoreFeedButton_Label,
                            onActivate: restoreFeed,
                            itemType: MenuItemType.Normal,
                            disabled: !isUserFeedAdmin(feed),
                            important: xsScreen ? false : undefined,
                          },
                          {
                            id: "permanent-delete-feed",
                            text: Res.DetailsTab_PermanentlyDeleteFeedButton_Label,
                            onActivate: permanentDeleteFeed,
                            iconProps: {
                              iconName: "Delete",
                            },
                            itemType: MenuItemType.Normal,
                            disabled: !isUserFeedAdmin(feed),
                            important: xsScreen ? false : undefined,
                          },
                        ]
                      : [
                          {
                            id: "delete-feed",
                            text: Res.DetailsTab_DeleteFeedButton_Label,
                            onActivate: deleteFeed,
                            itemType: MenuItemType.Normal,
                            disabled: !isUserPermittedToUseOperation(
                              FeedOperationPermissions.DeleteFeed,
                              feed,
                            ),
                          },
                        ]
                  }
                />
              );
            }}
          </ScreenSizeObserver>
          {feed && (
            <div className={"page-content page-content-top"}>
              <TabBar
                tabsClassName="settings-tabs"
                selectedTabId={selectedTab.tabId}
                onSelectedTabChanged={changeTab}
              >
                {tabDefinitions.map((tab, i) => {
                  return <Tab key={i} id={tab.tabId} name={tab.name} />;
                })}
              </TabBar>
              {CurrentTabComponent && (
                <div className={"page-content-top"}>
                  <CurrentTabComponent route={selectedTab.route} />
                </div>
              )}
            </div>
          )}
        </Page>
      )}
    </DefaultLoader>
  );
};
